// components
import { Icon } from '@sdb/planning-ui';
import { Box } from '@mui/material';

// styles
import {
  EntityActionButton,
  SelectedEntityContainer,
  SelectedEntityLabel,
  SelectedEntityTreatment
} from './SelectedEntity.styles';

// types
import { SelectedEntityProps } from './SelectedEntity.types';
import { AppointmentFormFieldData } from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import { MrpEntityKey } from '../../EntitySection.types';

// hooks
import { useFormContext, useWatch } from 'react-hook-form';

// utils
import dayjs from 'dayjs';
import {
  formatEntityDisplayLabel,
  formatTreatmentLabel
} from 'components/mrp/mrp.utils';

// stores
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useShallow } from 'zustand/shallow';

// i18n
import { useTranslation } from 'react-i18next';

const SelectedEntity = ({
  planningEntityType,
  entityIndex = 0
}: SelectedEntityProps) => {
  'use memo';
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });
  const { control, getValues, setValue } =
    useFormContext<AppointmentFormFieldData>();

  const fieldKey = `planEntities.${planningEntityType}` as MrpEntityKey;
  const entities = getValues(fieldKey);
  const entity = entities[entityIndex];

  const { calendarIdsPerEntityId, openCalendar, closeCalendar } =
    useCalendarStore(
      useShallow((state) => ({
        calendarIdsPerEntityId: state.calendarIdsPerEntityId,
        openCalendar: state.control.openCalendar,
        closeCalendar: state.control.closeCalendar
      }))
    );

  const openedInCalendarIds = calendarIdsPerEntityId[entity.entityId!] || [];

  const removeEntity = () => {
    const fields = getValues(fieldKey);
    const newFields = fields.filter((_, i) => i != entityIndex);

    openedInCalendarIds.forEach((id) => closeCalendar(id));

    setValue(fieldKey, newFields, { shouldValidate: true, shouldDirty: true });
  };

  const displayLabel = formatEntityDisplayLabel(entity.label, entity.number);

  // Only care receivers are on a treatment; the derived one lives on `original` because it is
  // read-only context as fetched, not state this form owns.
  const original = useAppointmentStore((state) => state.original);
  const treatment =
    planningEntityType === 'CLIENT'
      ? original?.derivedTreatments?.find(
          (derived) => derived.careReceiverId === entity.entityId
        )?.treatment
      : undefined;

  // The backend derived this for the date the appointment was fetched at, and nothing re-derives it
  // while the form is open. Once the planner moves the appointment to another day the value may no
  // longer be the treatment that day resolves to, and a stale treatment reads as confirmation that
  // the assignment is right — the opposite of what this feature is for. So show it only while the
  // form still sits on the date it was derived for, and show nothing otherwise rather than
  // re-deriving client-side, which would duplicate the rule the backend deliberately owns.
  // Time-of-day moves keep it: the derivation takes a date, not an instant.
  const start = useWatch({ name: 'start', control });
  const isDerivedForCurrentDate =
    !!start && !!original?.start && dayjs(start).isSame(original.start, 'day');

  const treatmentLabel =
    treatment && isDerivedForCurrentDate
      ? formatTreatmentLabel(
          treatment,
          t('appointmentForm.derivedTreatment.unnamed')
        )
      : undefined;

  return (
    <SelectedEntityContainer>
      <SelectedEntityLabel>
        {displayLabel}
        {treatmentLabel && (
          <SelectedEntityTreatment>{treatmentLabel}</SelectedEntityTreatment>
        )}
      </SelectedEntityLabel>

      <Box>
        <EntityActionButton
          tabIndex={-1}
          disabled={openedInCalendarIds.length > 0}
          variant="text"
        >
          <Icon
            type="calendar-month"
            fontSize="inherit"
            onClick={() =>
              openCalendar([
                {
                  label: entity.label,
                  planningEntityIdentifier: entity
                }
              ])
            }
          />
        </EntityActionButton>

        <EntityActionButton variant="text" tabIndex={-1}>
          <Icon type="xmark" fontSize="inherit" onClick={removeEntity} />
        </EntityActionButton>
      </Box>
    </SelectedEntityContainer>
  );
};

export default SelectedEntity;
